import React, { useState, useEffect } from 'react';
import {
  ChevronLeft, ChevronRight, Monitor, Wifi,
  BookOpen, Bell, Image, Calendar, Eye, EyeOff,
  Sparkles, Heart, Quote, User, Mic
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND}${url}`;
};

const typeConfig = {
  welcome:       { icon: Heart,     color: 'text-blue-200',    bg: 'bg-blue-500/20',    label: 'WELCOME' },
  rule:          { icon: BookOpen,  color: 'text-amber-300',   bg: 'bg-amber-500/20',   label: 'RULE' },
  quote:         { icon: Quote,     color: 'text-cyan-200',    bg: 'bg-cyan-500/20',    label: 'BIBLE QUOTE' },
  inspirational: { icon: Sparkles,  color: 'text-amber-100',   bg: 'bg-amber-500/30',   label: 'INSPIRATIONAL' },
  flyer:         { icon: Image,     color: 'text-emerald-300', bg: 'bg-emerald-500/20', label: 'FLYER' },
  announcement:  { icon: Bell,      color: 'text-red-300',     bg: 'bg-red-500/20',     label: 'ANNOUNCEMENT' },
  schedule:      { icon: Calendar,  color: 'text-blue-300',    bg: 'bg-blue-500/20',    label: 'SCHEDULE' },
};

const formatScheduleDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch { return iso; }
};

const PresentationRemote = () => {
  const [inputCode, setInputCode] = useState('');
  const [validated, setValidated] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [state, setState] = useState({ current_slide: 0, total_slides: 0, slides: [] });
  const [loading, setLoading] = useState(null);
  const [showPreview, setShowPreview] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get('code');
    if (urlCode) { setInputCode(urlCode); validateCode(urlCode); }
  }, []);

  useEffect(() => {
    if (!validated) return;
    const poll = setInterval(() => {
      fetch(`${API}/display/presentation/state`).then(r => r.json()).then(setState).catch(() => {});
    }, 1000);
    fetch(`${API}/display/presentation/state`).then(r => r.json()).then(setState).catch(() => {});
    return () => clearInterval(poll);
  }, [validated]);

  const validateCode = async (c) => {
    try {
      const res = await fetch(`${API}/display/presentation/validate/${c}`, { method: 'POST' });
      const data = await res.json();
      if (data.valid) { setCode(c); setValidated(true); setError(''); }
      else { setError('Invalid code. Try again.'); }
    } catch { setError('Connection error.'); }
  };

  const navigate = async (direction) => {
    setLoading(direction);
    try {
      await fetch(`${API}/display/presentation/navigate/${direction}`, { method: 'POST' });
      const res = await fetch(`${API}/display/presentation/state`);
      setState(await res.json());
    } catch (err) {
      console.warn('[PresentationRemote] navigate failed', err);
    }
    setLoading(null);
  };

  const goToSlide = async (index) => {
    setLoading(`goto-${index}`);
    try {
      await fetch(`${API}/display/presentation/goto/${index}`, { method: 'POST' });
      const res = await fetch(`${API}/display/presentation/state`);
      setState(await res.json());
    } catch (err) {
      console.warn('[PresentationRemote] goto failed', err);
    }
    setLoading(null);
  };

  // Code Entry
  if (!validated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 max-w-sm w-full text-center border border-white/20">
          <div className="w-16 h-16 bg-purple-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Monitor className="w-8 h-8 text-purple-300" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Presentation Remote</h1>
          <p className="text-white/60 text-sm mb-6">Enter the code from the presentation screen</p>
          <input
            type="text" maxLength={6} placeholder="Enter code"
            value={inputCode} onChange={(e) => setInputCode(e.target.value.toUpperCase())}
            data-testid="pres-remote-code-input"
            className="w-full text-center text-2xl font-mono tracking-[0.3em] bg-white/10 border-2 border-white/20 rounded-xl px-4 py-4 text-white placeholder-white/30 focus:border-purple-400 focus:outline-none mb-4"
          />
          {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
          <button onClick={() => validateCode(inputCode)} disabled={inputCode.length < 6}
            data-testid="pres-remote-connect-btn"
            className="w-full py-3 rounded-xl bg-purple-600 text-white font-bold text-lg hover:bg-purple-500 disabled:opacity-40 transition-all">
            Connect
          </button>
        </div>
      </div>
    );
  }

  const slides = state.slides || [];
  const current = state.current_slide || 0;
  const total = state.total_slides || 0;
  const currentSlideData = slides[current];
  const tc = typeConfig[currentSlideData?.type] || typeConfig.rule;
  const Icon = tc.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 pb-8">
      {/* Status */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Wifi className="w-4 h-4 text-emerald-400" />
          <span className="text-emerald-400 text-xs font-bold">CONNECTED</span>
        </div>
        <span className="text-white/40 text-xs font-mono">CODE: {code}</span>
      </div>

      {/* Current Slide Info */}
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 mb-4 border border-white/10" data-testid="pres-current-slide">
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-xs px-2 py-1 rounded-full font-bold ${tc.bg} ${tc.color}`}>{tc.label}</span>
          <span className="text-white text-sm font-bold">{current + 1} / {total}</span>
          <button
            onClick={() => setShowPreview(p => !p)}
            data-testid="toggle-preview-btn"
            className="ml-auto p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            title={showPreview ? 'Hide preview' : 'Show preview'}
          >
            {showPreview ? <EyeOff className="w-4 h-4 text-white/60" /> : <Eye className="w-4 h-4 text-white/60" />}
          </button>
        </div>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${tc.bg}`}><Icon className={`w-5 h-5 ${tc.color}`} /></div>
          <h2 className="text-white text-lg font-bold flex-1 truncate">{currentSlideData?.title || 'Loading...'}</h2>
        </div>
      </div>

      {/* Slide Content Preview — shows the FULL content that's currently
          on the TV so the operator/usher can read along on their phone. */}
      {showPreview && currentSlideData && (
        <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-4 mb-4 border border-white/10 max-h-[55vh] overflow-y-auto" data-testid="slide-content-preview">
          {/* Bible reference badge (quotes, inspirational) */}
          {currentSlideData.bible_reference && (
            <p className="text-amber-300 text-xs font-bold tracking-wider mb-2 italic" data-testid="slide-bible-ref">
              — {currentSlideData.bible_reference}
            </p>
          )}

          {/* Welcome / Rule / Quote / Inspirational / Announcement all share
              this text body. We render whatever non-empty content the slide has. */}
          {currentSlideData.content && currentSlideData.type !== 'schedule' && (
            <div className="text-white/90 text-sm leading-relaxed whitespace-pre-wrap" data-testid="slide-content-text">
              {currentSlideData.content}
            </div>
          )}

          {/* Flyer image — present on flyer + announcement slides */}
          {(currentSlideData.image_url || currentSlideData.flyer_url) && (
            <div className="mt-3 flex justify-center">
              <img
                src={resolveImageUrl(currentSlideData.image_url || currentSlideData.flyer_url)}
                alt={currentSlideData.title || 'Slide image'}
                className="max-h-[35vh] w-auto rounded-xl object-contain bg-black/30"
                data-testid="slide-image-preview"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
          )}

          {/* Schedule — show the FULL service breakdown (date + every assignment) */}
          {currentSlideData.type === 'schedule' && (
            <div className="space-y-3" data-testid="slide-schedule-preview">
              {currentSlideData.date && (
                <p className="text-white/70 text-sm font-semibold">{formatScheduleDate(currentSlideData.date)}</p>
              )}
              {currentSlideData.assignments && currentSlideData.assignments.length > 0 ? (
                <div className="space-y-2.5">
                  {currentSlideData.assignments.map((a, i) => {
                    const role = (a.role || '').toLowerCase();
                    const RoleIcon = role.includes('conductor') ? User
                      : role.includes('preacher') ? Mic
                      : BookOpen;
                    return (
                      <div key={i} className="flex items-start gap-3 bg-white/5 rounded-xl p-3 border border-white/10">
                        <div className="p-1.5 rounded-lg bg-white/10 shrink-0">
                          <RoleIcon className="w-4 h-4 text-amber-300" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white/50 text-[10px] font-bold uppercase tracking-wider">
                            {a.role_display || a.role}
                          </p>
                          <p className="text-white text-sm font-bold leading-snug">
                            {a.minister_name || <span className="text-white/30 italic font-normal">Not assigned</span>}
                          </p>
                          {a.bible_passage && (
                            <p className="text-amber-300 text-xs font-bold mt-0.5">{a.bible_passage}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-white/40 text-sm italic">No assignments yet for this service.</p>
              )}
            </div>
          )}

          {/* Fallback: nothing useful to show */}
          {!currentSlideData.content && !currentSlideData.image_url && !currentSlideData.flyer_url
            && currentSlideData.type !== 'schedule' && (
            <p className="text-white/40 text-sm text-center italic py-3">No preview available</p>
          )}
        </div>
      )}

      {/* "Up Next" peek — next 3 upcoming slides so the operator can plan
          their reading + transitions. Tap any pill to jump straight there. */}
      {(() => {
        const upcoming = slides.slice(current + 1, current + 4);
        if (!upcoming.length) return null;
        return (
          <div className="mb-4" data-testid="pres-up-next">
            <p className="text-white/50 text-[10px] font-black tracking-[0.18em] mb-1.5 px-1">UP NEXT</p>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
              {upcoming.map((s, idx) => {
                const cfg = typeConfig[s.type] || typeConfig.rule;
                const PeekIcon = cfg.icon;
                const absoluteIdx = current + 1 + idx;
                return (
                  <button
                    key={absoluteIdx}
                    onClick={() => goToSlide(absoluteIdx)}
                    data-testid={`pres-up-next-${absoluteIdx}`}
                    className="flex-shrink-0 min-w-[140px] max-w-[180px] text-left p-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 active:scale-95 transition-all"
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <div className={`p-1 rounded ${cfg.bg}`}>
                        <PeekIcon className={`w-3 h-3 ${cfg.color}`} />
                      </div>
                      <span className="text-[9px] font-black text-white/60 tracking-wider">
                        {absoluteIdx + 1}
                      </span>
                      <span className={`text-[9px] font-black tracking-wider ${cfg.color}`}>
                        {cfg.label}
                      </span>
                    </div>
                    <p className="text-white text-xs font-bold leading-snug line-clamp-2 overflow-hidden">
                      {s.title || '(untitled)'}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Main Navigation */}
      <div className="flex gap-3 mb-4">
        <button
          data-testid="pres-prev-slide"
          onClick={() => navigate('previous')}
          disabled={current <= 0}
          className="flex-1 py-5 rounded-2xl bg-white/10 text-white flex items-center justify-center gap-2 font-bold text-lg hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95"
        >
          <ChevronLeft className="w-7 h-7" /> PREV
        </button>
        <button
          data-testid="pres-next-slide"
          onClick={() => navigate('next')}
          disabled={current >= total - 1}
          className="flex-1 py-5 rounded-2xl bg-purple-600 text-white flex items-center justify-center gap-2 font-bold text-lg hover:bg-purple-500 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95"
        >
          NEXT <ChevronRight className="w-7 h-7" />
        </button>
      </div>

      {/* Slide List */}
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/10" data-testid="pres-slide-list">
        <p className="text-white/50 text-xs font-bold mb-3 tracking-wider">ALL SLIDES</p>
        <div className="space-y-1.5 max-h-[30vh] overflow-y-auto">
          {slides.map((slide, i) => {
            const st = typeConfig[slide.type] || typeConfig.rule;
            const SlideIcon = st.icon;
            const isActive = i === current;
            const isPast = i < current;
            return (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                data-testid={`pres-goto-${i}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-all text-left ${
                  isActive
                    ? 'bg-purple-600/40 border border-purple-400/50'
                    : isPast ? 'bg-white/5 opacity-50 hover:opacity-80' : 'bg-white/5 hover:bg-white/10'
                }`}
              >
                <span className={`w-6 text-center text-xs font-bold ${isActive ? 'text-white' : 'text-white/40'}`}>{i + 1}</span>
                <div className={`p-1 rounded-lg ${st.bg}`}><SlideIcon className={`w-3.5 h-3.5 ${st.color}`} /></div>
                <span className={`flex-1 text-sm truncate ${isActive ? 'text-white font-bold' : 'text-white/70'}`}>{slide.title}</span>
                {isActive && <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PresentationRemote;
